import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { Badge, gamificationService } from '../services/gamificationService';
import { tokenService } from '../services/tokenService';
import BadgeNotification from '../components/Gamification/BadgeNotification';

interface BadgeNotificationContextType {
    showBadgeNotification: (badge: Badge) => void;
    checkForNewBadges: () => Promise<void>;
}

const BadgeNotificationContext = createContext<BadgeNotificationContextType | undefined>(undefined);

export const useBadgeNotification = () => {
    const context = useContext(BadgeNotificationContext);
    if (!context) {
        throw new Error('useBadgeNotification must be used within a BadgeNotificationProvider');
    }
    return context;
};

interface BadgeNotificationProviderProps {
    children: React.ReactNode;
}

export const BadgeNotificationProvider: React.FC<BadgeNotificationProviderProps> = ({ children }) => {
    const [currentBadge, setCurrentBadge] = useState<Badge | null>(null);
    const [open, setOpen] = useState(false);
    const [badgeQueue, setBadgeQueue] = useState<Badge[]>([]);
    const lastKnownBadgeIds = useRef<Set<number>>(new Set());
    const isInitialized = useRef(false);

    // Show badge notification
    const showBadgeNotification = useCallback((badge: Badge) => {
        setBadgeQueue(prev => [...prev, badge]);
    }, []);

    // Process badge queue
    useEffect(() => {
        if (!open && badgeQueue.length > 0) {
            const [nextBadge, ...rest] = badgeQueue;
            setCurrentBadge(nextBadge);
            setOpen(true);
            setBadgeQueue(rest);
        }
    }, [open, badgeQueue]);

    // Handle close
    const handleClose = useCallback(() => {
        setOpen(false);
        // Small delay before showing next badge
        setTimeout(() => {
            setCurrentBadge(null);
        }, 300);
    }, []);

    // Check for new badges.
    // Token-Guard: ohne gültiges Token scheitert `getMyBadges()` mit 401,
    // was im Audit als "Failed to fetch" auftaucht — siehe Cluster E in
    // PUNCH-LIST-2026-04-24-final.md.
    const checkForNewBadges = useCallback(async () => {
        if (!tokenService.hasValidToken()) return;
        try {
            const userBadges = await gamificationService.getMyBadges();
            const currentBadgeIds = new Set(userBadges.map(ub => ub.badge.id));

            if (isInitialized.current) {
                // Find new badges
                userBadges.forEach(ub => {
                    if (!lastKnownBadgeIds.current.has(ub.badge.id)) {
                        showBadgeNotification(ub.badge);
                    }
                });
            }

            lastKnownBadgeIds.current = currentBadgeIds;
            isInitialized.current = true;
        } catch (error) {
            console.error('Error checking for new badges:', error);
        }
    }, [showBadgeNotification]);

    // Initial badge load and periodic check
    useEffect(() => {
        // Initial load
        checkForNewBadges();

        // Check every 30 seconds for new badges
        const interval = setInterval(checkForNewBadges, 30000);

        return () => clearInterval(interval);
    }, [checkForNewBadges]);

    return (
        <BadgeNotificationContext.Provider value={{ showBadgeNotification, checkForNewBadges }}>
            {children}
            <BadgeNotification
                badge={currentBadge}
                open={open}
                onClose={handleClose}
            />
        </BadgeNotificationContext.Provider>
    );
};

export default BadgeNotificationContext;
